import { PermissionAction } from "../../flatbuffers/generated/archive/permission-action";
import type { RoleScopeGrants, ScopeGrant } from "./scopeSlice";

/**
 * Wire body shape for a single scope grant (bank / lift / floor).
 * `entity_id` is a UUID string; `action` is the PermissionAction numeric value.
 * This matches the RoleRequest schema on the backend.
 */
export interface GrantBodyItem {
  entity_id: string;
  action: number;
}

/**
 * The per-entity grant arrays as the API expects them in a request body.
 */
export interface GrantBodyArrays {
  bank_permissions: GrantBodyItem[];
  lift_permissions: GrantBodyItem[];
  floor_permissions: GrantBodyItem[];
}

/**
 * Convert RoleScopeGrants (editor state) to the body arrays sent to the API.
 * One item per (entityId, action) pair — the backend replaces the whole set
 * on each PUT/POST.
 */
export function grantsToBodyArrays(grants: RoleScopeGrants): GrantBodyArrays {
  return {
    bank_permissions: grantsToBodyItems(grants.bankGrants),
    lift_permissions: grantsToBodyItems(grants.liftGrants),
    floor_permissions: grantsToBodyItems(grants.floorGrants),
  };
}

function grantsToBodyItems(grants: ScopeGrant[]): GrantBodyItem[] {
  return grants.map((g) => ({
    entity_id: g.entityId,
    action: g.action,
  }));
}

/**
 * Convert body arrays (from the API response or save payload) back to
 * RoleScopeGrants (editor state).
 *
 * Tolerant of missing fields — returns empty arrays (allow-all) when absent.
 */
export function bodyArraysToGrants(body: Record<string, unknown>): RoleScopeGrants {
  return {
    bankGrants: bodyItemsToGrants(body.bank_permissions),
    liftGrants: bodyItemsToGrants(body.lift_permissions),
    floorGrants: bodyItemsToGrants(body.floor_permissions),
  };
}

function bodyItemsToGrants(raw: unknown): ScopeGrant[] {
  if (!Array.isArray(raw)) return [];
  return (raw as unknown[]).reduce<ScopeGrant[]>((acc, item) => {
    if (!item || typeof item !== "object") return acc;
    const entityId = String((item as Record<string, unknown>).entity_id ?? "");
    if (!entityId) return acc;
    const actionRaw = (item as Record<string, unknown>).action;
    const actionNum = typeof actionRaw === "number" ? actionRaw : Number(actionRaw ?? 0);
    acc.push({ entityId, action: toPermissionAction(actionNum) });
    return acc;
  }, []);
}

function toPermissionAction(n: number): PermissionAction {
  if (n === PermissionAction.CREATE) return PermissionAction.CREATE;
  if (n === PermissionAction.UPDATE) return PermissionAction.UPDATE;
  if (n === PermissionAction.DELETE) return PermissionAction.DELETE;
  if (n === PermissionAction.ACTION) return PermissionAction.ACTION;
  return PermissionAction.VIEW;
}
